import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './CDRDashboard.css';
import DismissibleNotice from './DismissibleNotice';
import {
  createCDROperation,
  deleteCDROperation,
  getCDR,
  getCDROpportunities,
  updateCDROperation,
  updateCDRSettings,
} from '../api/cdr';
import { getOptionsChain } from '../api/options';

const OPERATION_TYPES = [
  ['put_sale', 'Venda de PUT'],
  ['put_repurchase', 'Recompra de PUT'],
  ['put_assignment', 'Exercício de PUT'],
  ['put_expiry', 'Vencimento de PUT sem exercício'],
  ['stock_buy', 'Compra de ações'],
  ['call_sale', 'Venda de CALL'],
  ['call_repurchase', 'Recompra de CALL'],
  ['call_assignment', 'Exercício de CALL'],
  ['call_expiry', 'Vencimento de CALL sem exercício'],
  ['stock_sale', 'Venda manual de ações'],
  ['dividend', 'Dividendos'],
  ['jcp', 'Juros sobre capital próprio'],
  ['fee', 'Taxa'],
  ['tax', 'Imposto'],
];

const OPTION_TYPES = new Set([
  'put_sale', 'put_repurchase', 'put_assignment', 'put_expiry',
  'call_sale', 'call_repurchase', 'call_assignment', 'call_expiry',
]);
const AMOUNT_TYPES = new Set(['dividend', 'jcp', 'fee', 'tax']);
const PHASE_TONES = {
  cash: '#6b7fa0',
  put_sold: '#a78bfa',
  put_assigned: '#f59e0b',
  stock_owned: '#38bdf8',
  call_sold: '#34d399',
  call_assigned: '#facc15',
};

const fmtBRL = value => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const fmtPct = value => value == null || Number.isNaN(Number(value))
  ? '—'
  : `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(2)}%`;
const fmtDate = value => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR')
  : '—';
const fmtOptionDate = value => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR')
  : '—';
const fmtNumber = (value, digits = 2) => value == null || Number.isNaN(Number(value))
  ? '—'
  : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtDateTime = value => value
  ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  : 'aguardando atualização';
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_OPPORTUNITY_FILTERS = {
  type: '',
  minPremium: '',
  maxPremium: '',
  minStrike: '',
  maxStrike: '',
  minDays: '',
  maxDays: '',
  minLiquidity: '',
  maxCapital: '',
};

function OptionOpportunitiesPanel({
  data,
  loading,
  refreshing,
  error,
  filters,
  onFilterChange,
  onApply,
  onClear,
  onRefresh,
  onSelectTicker,
  selectedContract,
  onSelectContract,
}) {
  const ranges = data?.filters?.ranges || {};
  const filterCount = Object.values(filters).filter(value => value !== '').length;
  const assets = data?.assets || [];
  const contracts = data?.contracts || [];
  const selectedAsset = assets.find(asset => asset.symbol === data?.selectedTicker);
  const numericRange = field => ranges[field]?.min != null
    ? `Entre ${fmtNumber(ranges[field].min)} e ${fmtNumber(ranges[field].max)}`
    : 'Sem dados nesta atualização';

  return (
    <section className="cdr-opportunities" aria-labelledby="cdr-opportunities-title">
      <div className="cdr-opportunities-head">
        <div>
          <span className="cdr-eyebrow">SCANNER / THE WHEEL</span>
          <h2 id="cdr-opportunities-title">Contratos atuais para estudo</h2>
          <p>Compare PUTs com garantia em caixa e CALLs cobertas a partir da cadeia de opções disponível agora.</p>
        </div>
        <div className="cdr-opportunities-status">
          <span className="cdr-data-source">{data?.source || 'Fonte de opções'}</span>
          {data?.marketDate && <span>Pregão de referência: {fmtOptionDate(data.marketDate)}</span>}
          <span>Atualizado: {fmtDateTime(data?.asOf)}</span>
          <button className="cdr-secondary-button" type="button" onClick={onRefresh} disabled={loading || refreshing}>
            {refreshing ? 'Atualizando...' : 'Atualizar dados'}
          </button>
        </div>
      </div>

      <div className="cdr-education-note" role="note">
        <strong>Leitura educativa</strong>
        <span>Leitura educativa calculada pelo FatCat a partir de dados da BRAPI. Não é recomendação de investimento e nenhuma ordem é enviada.</span>
      </div>
      {data?.providerUnavailable && <div className="cdr-opportunity-warning" role="alert"><strong>Provider indisponível.</strong> {data.message || 'A fonte de opções não respondeu; nenhuma oportunidade deve ser inferida.'}</div>}

      {error && data && <DismissibleNotice noticeKey="cdr-opportunity-refresh-error" eventKey={error} closeLabel="Fechar aviso de atualização das opções" className="cdr-opportunity-warning" role="status">{error} A última leitura continua visível.</DismissibleNotice>}
      {data?.partial && <DismissibleNotice noticeKey="cdr-opportunity-partial" eventKey="partial" closeLabel="Fechar aviso de atualização parcial" className="cdr-opportunity-warning" role="status">Nem todos os vencimentos puderam ser atualizados; a tabela mostra apenas as séries retornadas pela fonte.</DismissibleNotice>}
      {data?.limitedByProvider && <DismissibleNotice noticeKey="cdr-opportunity-provider-limit" eventKey="limited" closeLabel="Fechar aviso de limitação da fonte" className="cdr-opportunity-warning" role="status">A fonte atual disponibilizou cadeias para parte do universo de ativos. A lista mostra apenas os ativos que puderam ser consultados agora.</DismissibleNotice>}

      <div className="cdr-opportunity-assets" aria-label="Ativos elegíveis">
        <div className="cdr-opportunity-summary">
          <strong>{assets.length} ativo(s) elegível(is)</strong>
          <span>{contracts.length} contrato(s) visível(is)</span>
          {selectedAsset?.spot && <span>Spot {fmtBRL(selectedAsset.spot)}</span>}
        </div>
        <div className="cdr-opportunity-asset-list">
           {assets.map(asset => (
             <div className="cdr-opportunity-asset-group" key={asset.symbol}>
               <button
                 type="button"
                 className={`cdr-opportunity-asset ${asset.symbol === data?.selectedTicker ? 'selected' : ''}`}
                 onClick={() => onSelectTicker(asset.symbol)}
                 data-testid={`button-opportunity-asset-${asset.symbol}`}
               >
                 <strong>{asset.symbol}</strong>
                 <span>{asset.name}</span>
                 <small>{asset.spot ? `Spot ${fmtBRL(asset.spot)}` : 'Spot indisponível'}</small>
               </button>
               <button type="button" className="cdr-option-chain-link" onClick={() => { window.location.href = `/opcoes/${encodeURIComponent(asset.symbol)}?strategy=wheel_put`; }}>Grade de opções</button>
             </div>
           ))}
          {!assets.length && !loading && <span className="cdr-opportunity-muted">Nenhum ativo elegível nesta atualização.</span>}
        </div>
      </div>

      <form className="cdr-opportunity-filters" onSubmit={event => { event.preventDefault(); onApply(); }} role="search" aria-label="Filtros de oportunidades The Wheel">
        <div className="cdr-filter-title">
          <strong>Filtros de leitura</strong>
          {filterCount > 0 && <span>{filterCount} ativo(s)</span>}
        </div>
        <label>
          <span>Tipo</span>
          <select value={filters.type} onChange={event => onFilterChange('type', event.target.value)} data-testid="select-opportunity-type">
            <option value="">PUT e CALL coberta</option>
            <option value="PUT">PUT</option>
            <option value="CALL">CALL coberta</option>
          </select>
        </label>
        <label>
          <span>Prêmio mín. (R$)</span>
          <input type="number" min="0" step="0.01" value={filters.minPremium} onChange={event => onFilterChange('minPremium', event.target.value)} placeholder={numericRange('premium')} data-testid="input-opportunity-min-premium" />
        </label>
        <label>
          <span>Prêmio máx. (R$)</span>
          <input type="number" min="0" step="0.01" value={filters.maxPremium} onChange={event => onFilterChange('maxPremium', event.target.value)} placeholder="Sem limite" />
        </label>
        <label>
          <span>Strike mín. (R$)</span>
          <input type="number" min="0" step="0.01" value={filters.minStrike} onChange={event => onFilterChange('minStrike', event.target.value)} placeholder={numericRange('strike')} />
        </label>
        <label>
          <span>Strike máx. (R$)</span>
          <input type="number" min="0" step="0.01" value={filters.maxStrike} onChange={event => onFilterChange('maxStrike', event.target.value)} placeholder="Sem limite" />
        </label>
        <label>
          <span>Vencimento mín. (dias)</span>
          <input type="number" min="0" step="1" value={filters.minDays} onChange={event => onFilterChange('minDays', event.target.value)} placeholder={numericRange('daysToMaturity')} />
        </label>
        <label>
          <span>Vencimento máx. (dias)</span>
          <input type="number" min="0" step="1" value={filters.maxDays} onChange={event => onFilterChange('maxDays', event.target.value)} placeholder="Sem limite" />
        </label>
        <label>
          <span>Liquidez mín. (R$)</span>
          <input type="number" min="0" step="1000" value={filters.minLiquidity} onChange={event => onFilterChange('minLiquidity', event.target.value)} placeholder={numericRange('liquidity')} />
        </label>
        <label>
          <span>Capital máx. (R$)</span>
          <input type="number" min="0" step="100" value={filters.maxCapital} onChange={event => onFilterChange('maxCapital', event.target.value)} placeholder={numericRange('capitalRequired')} />
        </label>
        <div className="cdr-filter-actions">
          <button className="cdr-primary-button" type="submit" disabled={loading}>{loading ? 'Consultando...' : 'Aplicar filtros'}</button>
          {filterCount > 0 && <button className="cdr-secondary-button" type="button" onClick={onClear}>Limpar</button>}
        </div>
      </form>

      {loading && !data ? (
        <div className="cdr-opportunity-loading" aria-label="Carregando contratos">
          {[1, 2, 3, 4, 5].map(item => <div className="cdr-opportunity-skeleton" key={item} />)}
        </div>
      ) : error && !data ? (
        <div className="cdr-opportunity-empty cdr-opportunity-error" role="alert">
          <strong>Não foi possível carregar os contratos.</strong>
          <p>{error}</p>
          <button className="cdr-secondary-button" type="button" onClick={onRefresh}>Tentar novamente</button>
        </div>
      ) : data?.providerUnavailable ? (
        <div className="cdr-opportunity-empty cdr-opportunity-error" role="status">
          <strong>Cadeia indisponível no plano atual.</strong>
          <p>{data.message || 'A fonte de opções não está habilitada para este plano. Isso não permite concluir que não existam contratos.'}</p>
        </div>
      ) : !contracts.length ? (
        <div className="cdr-opportunity-empty">
          <strong>{filterCount ? 'Nenhum contrato corresponde à leitura atual.' : 'Nenhum contrato elegível nesta atualização.'}</strong>
          <p>{filterCount ? 'Ajuste os limites ou limpe os filtros para comparar os dados disponíveis.' : 'A fonte não retornou PUTs abaixo do spot ou CALLs cobertas acima do spot para este ativo.'}</p>
          {filterCount && <button className="cdr-secondary-button" type="button" onClick={onClear}>Limpar filtros</button>}
        </div>
      ) : (
        <div className="cdr-opportunity-table-wrap">
          <table className="cdr-opportunity-table">
            <caption>Contratos elegíveis para comparação educativa, ordenados por vencimento mais próximo</caption>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Contrato</th>
                <th>Strike</th>
                <th>Distância do spot</th>
                <th>Prêmio ref.</th>
                <th>Vencimento</th>
                <th>Liquidez</th>
                <th>Capital requerido</th>
                <th>Delta</th>
                <th>IV Rank</th>
                <th>Yield anual.</th>
                <th>Prob. exercício</th>
                <th>Leitura</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(contract => (
                <tr
                  key={`${contract.optionSymbol}-${contract.strike}-${contract.optionType}`}
                  className={selectedContract?.optionSymbol === contract.optionSymbol ? 'selected' : ''}
                  onClick={() => onSelectContract(contract)}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelectContract(contract); } }}
                  tabIndex="0"
                  data-testid={`row-opportunity-${contract.optionSymbol || contract.strike}`}
                >
                  <td><span className={`cdr-option-type ${(contract.optionType || '').toLowerCase()}`}>{contract.optionType || '—'}</span></td>
                  <td><strong className="cdr-option-symbol">{contract.optionSymbol || 'Contrato sem símbolo'}</strong></td>
                  <td className="cdr-number">{fmtBRL(contract.strike)}</td>
                  <td className="cdr-number"><strong>{contract.distancePct != null ? `${fmtNumber(contract.distancePct)}%` : '—'}</strong></td>
                  <td className="cdr-number"><strong className="cdr-premium-value">{fmtBRL(contract.premium)}</strong><small>{fmtBRL(contract.premiumTotal)} total</small></td>
                  <td><strong>{fmtOptionDate(contract.expiration)}</strong><small>{contract.daysToMaturity != null ? `${contract.daysToMaturity} dias` : 'prazo indisponível'}</small></td>
                  <td className="cdr-number">{fmtBRL(contract.liquidity, 0)}</td>
                  <td className="cdr-number">{contract.capitalRequired != null ? fmtBRL(contract.capitalRequired) : '—'}<small>{contract.premiumYieldPct != null ? `${fmtNumber(contract.premiumYieldPct)}% sobre capital` : 'sem spot'}</small></td>
                  <td className="cdr-number">{contract.delta == null ? '—' : fmtNumber(contract.delta)}</td>
                  <td className="cdr-number">{contract.ivRank == null ? '—' : `${fmtNumber(contract.ivRank)}%`}</td>
                  <td className="cdr-number">{contract.annualizedYield == null ? '—' : `${fmtNumber(contract.annualizedYield)}%`}</td>
                  <td className="cdr-number">{contract.probItm == null ? '—' : `${fmtNumber(contract.probItm)}%`}</td>
                  <td><span className="cdr-opportunity-reading">{contract.direction}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedContract && (
        <aside className="cdr-opportunity-detail" role="dialog" aria-labelledby="cdr-opportunity-detail-title">
          <div>
            <span className="cdr-eyebrow">DETALHE EDUCATIVO</span>
            <h3 id="cdr-opportunity-detail-title">{selectedContract.symbol || 'Contrato selecionado'}</h3>
          </div>
          <button className="cdr-icon-button" type="button" onClick={() => onSelectContract(null)} aria-label="Fechar detalhes">×</button>
          <div className="cdr-opportunity-detail-grid">
            <span>Tipo<strong>{selectedContract.category}</strong></span>
            <span>Strike<strong>{fmtBRL(selectedContract.strike)}</strong></span>
            <span>Prêmio de referência<strong>{fmtBRL(selectedContract.premium)} / ação</strong></span>
            <span>Vencimento<strong>{fmtOptionDate(selectedContract.dueDate)} · {selectedContract.daysToMaturity ?? '—'} dias</strong></span>
            <span>Liquidez<strong>{fmtBRL(selectedContract.liquidity, 0)}</strong></span>
            <span>Capital requerido<strong>{selectedContract.capitalRequired != null ? fmtBRL(selectedContract.capitalRequired) : '—'}</strong></span>
          </div>
          <p>O capital requerido é uma referência: strike × tamanho do contrato para PUTs e spot × tamanho do contrato para CALLs cobertas. O prêmio usa preferencialmente o bid disponível, sem considerar custos, impostos ou execução.</p>
          <small>{data?.disclaimer}</small>
        </aside>
      )}
    </section>
  );
}

function expiryStatusLabel(event) {
  if (event.status === 'missing') return 'Vencimento não informado';
  if (event.status === 'overdue') return `${Math.abs(event.daysToExpiry)} dia(s) em atraso`;
  if (event.daysToExpiry === 0) return 'Vence hoje';
  return `${event.daysToExpiry} dia(s) para vencer`;
}

function ExpiryPlanningPanel({ planning, selectedEventId, onSelectEvent }) {
  const events = planning?.events || [];
  const selected = events.find(event => event.id === selectedEventId) || events[0] || null;
  const scenarios = selected ? Object.values(selected.scenarios || {}) : [];

  return (
    <section className="cdr-expiry-planning" aria-labelledby="cdr-expiry-title">
      <div className="cdr-expiry-head">
        <div>
          <span className="cdr-eyebrow">PLANEJAMENTO DE VENCIMENTOS</span>
          <h2 id="cdr-expiry-title">Prepare o próximo ciclo antes do vencimento</h2>
          <p>Os cenários usam apenas as operações registradas e as cotações que estiverem disponíveis. A decisão e o lançamento continuam manuais.</p>
        </div>
        <div className="cdr-expiry-summary">
          <span><strong>{planning?.metrics?.upcomingEvents || 0}</strong> contrato(s) aberto(s)</span>
          <span>Capital envolvido <strong>{fmtBRL(planning?.metrics?.capitalInvolved)}</strong></span>
          {planning?.metrics?.capitalUnavailableEvents > 0 && <span>Capital parcial: <strong>{planning.metrics.capitalUnavailableEvents}</strong> sem spot</span>}
          <span className={planning?.metrics?.exerciseRiskEvents ? 'risk' : ''}><strong>{planning?.metrics?.exerciseRiskEvents || 0}</strong> em atenção</span>
        </div>
      </div>

      <div className="cdr-education-note" role="note">
        <strong>Planejamento, não execução</strong>
        <span>{planning?.disclaimer || 'A CDR não envia ordens nem substitui sua decisão sobre exercício, recompra ou rolagem.'}</span>
      </div>

      {!events.length ? (
        <div className="cdr-expiry-empty">
          <strong>Nenhum vencimento ativo para planejar.</strong>
          <p>Registre uma venda de PUT ou CALL com vencimento para acompanhar prazo, capital envolvido e cenários de fechamento.</p>
        </div>
      ) : (
        <div className="cdr-expiry-workspace">
          <div className="cdr-expiry-list" role="list" aria-label="Calendário de vencimentos">
            <div className="cdr-expiry-list-heading">
              <span>Contrato</span><span>Vencimento</span><span>Capital</span><span>Risco</span>
            </div>
            {events.map(event => (
              <button
                type="button"
                role="listitem"
                key={event.id}
                className={`cdr-expiry-event ${selected?.id === event.id ? 'selected' : ''} ${event.status}`}
                onClick={() => onSelectEvent(event.id)}
                data-testid={`expiry-event-${event.ticker}-${event.optionType}`}
              >
                <span className="cdr-expiry-contract">
                  <strong>{event.ticker} · {event.optionType}</strong>
                  <small>{event.optionSymbol || `Strike ${fmtBRL(event.strike)}`} · {event.contracts} contrato(s)</small>
                </span>
                <span className="cdr-expiry-date">
                  <strong>{fmtDate(event.expiryDate)}</strong>
                  <small>{expiryStatusLabel(event)}</small>
                </span>
                <span className="cdr-expiry-capital">{fmtBRL(event.capitalInvolved)}</span>
                <span className={`cdr-risk-badge ${event.exerciseRisk?.tone}`}>{event.exerciseRisk?.label || 'Sem leitura'}</span>
              </button>
            ))}
          </div>

          {selected && (
            <aside className="cdr-expiry-detail" aria-labelledby="cdr-scenario-title">
              <div className="cdr-expiry-detail-head">
                <div>
                  <span className="cdr-eyebrow">CENÁRIOS DE PLANEJAMENTO</span>
                  <h3 id="cdr-scenario-title">{selected.ticker} · {selected.optionSymbol || `${selected.optionType} ${fmtBRL(selected.strike)}`}</h3>
                </div>
                <span className={`cdr-risk-badge ${selected.exerciseRisk?.tone}`}>{selected.exerciseRisk?.label}</span>
              </div>
              <div className="cdr-expiry-facts">
                <span>Vence em <strong>{expiryStatusLabel(selected)}</strong></span>
                <span>Strike <strong>{fmtBRL(selected.strike)}</strong></span>
                <span>Spot disponível <strong>{fmtBRL(selected.currentPrice)}</strong></span>
                <span>Valor intrínseco <strong>{fmtBRL(selected.intrinsicValue)}</strong></span>
                <span>Prêmio registrado <strong>{fmtBRL(selected.premiumReceived)}</strong></span>
                <span>Capital envolvido <strong>{fmtBRL(selected.capitalInvolved)}</strong></span>
              </div>
              <div className="cdr-scenario-grid">
                {scenarios.map(scenario => (
                  <article className={`cdr-scenario ${scenario.available ? '' : 'unavailable'}`} key={scenario.label}>
                    <div>
                      <span>{scenario.label}</span>
                      <strong className={scenario.result != null && scenario.result < 0 ? 'negative' : 'positive'}>
                        {scenario.result == null ? 'Cotação indisponível' : fmtBRL(scenario.result)}
                      </strong>
                    </div>
                    {scenario.quote != null && <small>Referência da opção: {fmtBRL(scenario.quote)} / ação</small>}
                    {scenario.targetExpiry && <small>Novo vencimento: {fmtDate(scenario.targetExpiry)}</small>}
                    <p>{scenario.detail}</p>
                  </article>
                ))}
              </div>
              <p className="cdr-expiry-disclaimer">Resultados brutos, sem taxas, impostos, slippage ou garantia de liquidez. Registrar uma operação continua sendo uma ação separada.</p>
            </aside>
          )}
        </div>
      )}
    </section>
  );
}

function OperationModal({ operation, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    operation_type: operation?.operation_type || 'put_sale',
    ticker: operation?.ticker || '',
    name: operation?.name || '',
    option_symbol: operation?.option_symbol || '',
    trade_date: operation?.trade_date ? String(operation.trade_date).slice(0, 10) : today(),
    expiry_date: operation?.expiry_date ? String(operation.expiry_date).slice(0, 10) : '',
    quantity: operation?.quantity || 1,
    price: operation?.price ?? '',
    amount: operation?.amount ?? '',
    strike: operation?.strike ?? '',
    contract_multiplier: operation?.contract_multiplier || 100,
    notes: operation?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [optionsState, setOptionsState] = useState({ loading: false, message: '' });
  const isOption = OPTION_TYPES.has(form.operation_type);
  const amountOnly = AMOUNT_TYPES.has(form.operation_type);
  const settlementAtStrike = ['put_assignment', 'call_assignment'].includes(form.operation_type);
  const close = () => !saving && onClose();
  const optionSide = form.operation_type.startsWith('call_') ? 'CALL' : 'PUT';

  useEffect(() => {
    const ticker = form.ticker.trim().toUpperCase();
    if (!isOption || ticker.length < 4) {
      setAvailableOptions([]);
      setOptionsState({ loading: false, message: '' });
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setOptionsState({ loading: true, message: 'Carregando cadeia disponível…' });
      const purpose = optionSide === 'CALL' ? 'entrada_call' : 'wheel_put';
      getOptionsChain(ticker, purpose)
        .then(result => {
          if (!active) return;
          const contracts = (result?.contracts || []).filter(contract => String(contract.optionType || contract.option_type).toUpperCase() === optionSide);
          setAvailableOptions(contracts);
          setOptionsState({
            loading: false,
            message: contracts.length
              ? `${contracts.length} contrato(s) disponível(is) no snapshot atual.`
              : result?.availabilityReason || 'Nenhum contrato elegível no snapshot atual. Digitação manual disponível.',
          });
        })
        .catch(err => {
          if (!active) return;
          setAvailableOptions([]);
          setOptionsState({ loading: false, message: err?.response?.data?.error || 'Cadeia indisponível. Digitação manual disponível.' });
        });
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [form.ticker, isOption, optionSide]);

  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const selectAvailableOption = symbol => {
    const contract = availableOptions.find(item => (item.optionSymbol || item.option_symbol) === symbol);
    if (!contract) return;
    setForm(current => ({
      ...current,
      option_symbol: symbol,
      strike: contract.strike ?? current.strike,
      expiry_date: String(contract.expiration || '').slice(0, 10),
      price: contract.mid ?? contract.last ?? current.price,
      contract_multiplier: contract.contractSize || contract.contract_size || 100,
    }));
  };
  const submit = async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        ticker: form.ticker.trim().toUpperCase(),
        name: form.name.trim(),
        option_symbol: form.option_symbol.trim().toUpperCase(),
        expiry_date: form.expiry_date || null,
        price: form.price === '' ? null : Number(form.price),
        amount: form.amount === '' ? null : Number(form.amount),
        strike: form.strike === '' ? null : Number(form.strike),
        quantity: Number(form.quantity),
        contract_multiplier: Number(form.contract_multiplier),
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível salvar a operação.');
      setSaving(false);
    }
  };

  return (
    <div className="cdr-overlay" role="dialog" aria-modal="true" aria-label="Registrar operação CDR" onMouseDown={close}>
      <form className="cdr-modal" onMouseDown={event => event.stopPropagation()} onSubmit={submit}>
        <header className="cdr-modal-head">
          <div>
            <span className="cdr-eyebrow">CDR / The Wheel</span>
            <h2>{operation ? 'Editar operação' : 'Registrar operação'}</h2>
          </div>
          <button type="button" className="cdr-icon-button" onClick={close} aria-label="Fechar">×</button>
        </header>
        <div className="cdr-form-grid">
          <label className="cdr-field cdr-field-wide">
            <span>Tipo de operação</span>
            <select value={form.operation_type} onChange={event => set('operation_type', event.target.value)}>
              {OPERATION_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="cdr-field">
            <span>Ativo</span>
            <input value={form.ticker} maxLength="12" required placeholder="PETR4" onChange={event => set('ticker', event.target.value)} />
          </label>
          <label className="cdr-field">
            <span>Nome (opcional)</span>
            <input value={form.name} placeholder="Petrobras PN" onChange={event => set('name', event.target.value)} />
          </label>
          <label className="cdr-field">
            <span>Data da operação</span>
            <input type="date" value={form.trade_date} required onChange={event => set('trade_date', event.target.value)} />
          </label>
          <label className="cdr-field">
            <span>{isOption ? 'Contratos' : 'Quantidade'}</span>
            <input type="number" min="1" step="1" value={form.quantity} required onChange={event => set('quantity', event.target.value)} />
          </label>
          {isOption && (
            <>
              <label className="cdr-field cdr-field-wide">
                <span>Selecionar opção disponível</span>
                <select
                  value={availableOptions.some(item => (item.optionSymbol || item.option_symbol) === form.option_symbol) ? form.option_symbol : ''}
                  disabled={optionsState.loading || !availableOptions.length}
                  onChange={event => selectAvailableOption(event.target.value)}
                >
                  <option value="">{optionsState.loading ? 'Carregando…' : availableOptions.length ? 'Escolha um contrato' : 'Nenhuma opção carregada'}</option>
                  {availableOptions.map(contract => {
                    const symbol = contract.optionSymbol || contract.option_symbol;
                    return <option key={symbol} value={symbol}>
                      {symbol} · Strike {fmtBRL(contract.strike)} · Venc. {fmtOptionDate(contract.expiration)} · Delta {fmtNumber(contract.delta)} · Prêmio {fmtBRL(contract.mid ?? contract.last)}
                    </option>;
                  })}
                </select>
                <small className="cdr-option-picker-status">{optionsState.message || 'Informe o ativo para consultar a cadeia persistida.'}</small>
              </label>
              <label className="cdr-field">
                <span>Símbolo da opção (ou digite manualmente)</span>
                <input value={form.option_symbol} placeholder="PETRA340" onChange={event => set('option_symbol', event.target.value)} />
              </label>
              <label className="cdr-field">
                <span>Strike</span>
                <input type="number" min="0" step="0.01" value={form.strike} required onChange={event => set('strike', event.target.value)} />
              </label>
              <label className="cdr-field">
                <span>Vencimento</span>
                <input type="date" value={form.expiry_date} onChange={event => set('expiry_date', event.target.value)} />
              </label>
              <label className="cdr-field">
                <span>Multiplicador</span>
                <input type="number" min="1" step="1" value={form.contract_multiplier} onChange={event => set('contract_multiplier', event.target.value)} />
              </label>
            </>
          )}
          {!amountOnly && !settlementAtStrike && (
            <label className="cdr-field">
              <span>Preço unitário</span>
              <input type="number" min="0" step="0.01" value={form.price} placeholder="0,00" onChange={event => set('price', event.target.value)} />
            </label>
          )}
          {!settlementAtStrike && <label className="cdr-field">
            <span>{amountOnly ? 'Valor total' : 'Valor total (opcional)'}</span>
            <input type="number" min="0" step="0.01" value={form.amount} required={amountOnly} placeholder="0,00" onChange={event => set('amount', event.target.value)} />
          </label>}
          {settlementAtStrike && <p className="cdr-settlement-note">A liquidação é calculada automaticamente pelo strike, quantidade e multiplicador da opção.</p>}
          <label className="cdr-field cdr-field-wide">
            <span>Observações</span>
            <textarea rows="3" value={form.notes} placeholder="Tese, risco, motivo da operação ou detalhe da rolagem..." onChange={event => set('notes', event.target.value)} />
          </label>
        </div>
        {error && <p className="cdr-form-error">{error}</p>}
        <footer className="cdr-modal-actions">
          <button type="button" className="cdr-secondary-button" onClick={close}>Cancelar</button>
          <button className="cdr-primary-button" disabled={saving}>{saving ? 'Salvando...' : 'Salvar operação'}</button>
        </footer>
      </form>
    </div>
  );
}

function SettingsModal({ settings, onClose, onSave }) {
  const [initialCapital, setInitialCapital] = useState(settings?.initial_capital ?? 0);
  const [monthlyGoal, setMonthlyGoal] = useState(settings?.monthly_premium_goal ?? '');
  const [expiryAlertsEnabled, setExpiryAlertsEnabled] = useState(!!settings?.expiry_alerts_enabled);
  const [expiryAlertDays, setExpiryAlertDays] = useState(settings?.expiry_alert_days ?? 3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave({
        initial_capital: Number(initialCapital),
        monthly_premium_goal: monthlyGoal === '' ? null : Number(monthlyGoal),
        expiry_alerts_enabled: expiryAlertsEnabled,
        expiry_alert_days: Number(expiryAlertDays),
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível salvar as configurações.');
      setSaving(false);
    }
  };
  return (
    <div className="cdr-overlay" role="dialog" aria-modal="true" aria-label="Configurar capital CDR" onMouseDown={() => !saving && onClose()}>
      <form className="cdr-modal cdr-settings-modal" onMouseDown={event => event.stopPropagation()} onSubmit={submit}>
        <header className="cdr-modal-head">
          <div>
            <span className="cdr-eyebrow">CDR / CONFIGURAÇÃO</span>
            <h2>Capital e meta de prêmio</h2>
          </div>
          <button type="button" className="cdr-icon-button" onClick={onClose} aria-label="Fechar">×</button>
        </header>
        <p className="cdr-modal-copy">Esses valores são apenas referências de planejamento. A CDR não envia ordens nem movimenta recursos.</p>
        <div className="cdr-form-grid">
          <label className="cdr-field">
            <span>Capital inicial</span>
            <input type="number" min="0" step="0.01" value={initialCapital} required onChange={event => setInitialCapital(event.target.value)} />
          </label>
          <label className="cdr-field">
            <span>Meta mensal de prêmios</span>
            <input type="number" min="0" step="0.01" value={monthlyGoal} placeholder="Opcional" onChange={event => setMonthlyGoal(event.target.value)} />
          </label>
          <div className="cdr-settings-alerts cdr-field-wide">
            <label className="cdr-alert-toggle">
              <input
                type="checkbox"
                checked={expiryAlertsEnabled}
                onChange={event => setExpiryAlertsEnabled(event.target.checked)}
              />
              <span>
                <strong>Receber lembretes de vencimento</strong>
                <small>Avise antes de vencer uma PUT ou CALL aberta com vencimento registrado.</small>
              </span>
            </label>
            <label className="cdr-field">
              <span>Antecedência do lembrete (dias)</span>
              <input
                type="number"
                min="1"
                max="30"
                step="1"
                value={expiryAlertDays}
                onChange={event => setExpiryAlertDays(event.target.value)}
                disabled={!expiryAlertsEnabled}
              />
            </label>
            <p>O lembrete resume ativo, vencimento, capital envolvido e risco de exercício. Ele não envia ordens. A entrega usa os canais de alerta ativados no seu Perfil.</p>
          </div>
        </div>
        {error && <p className="cdr-form-error">{error}</p>}
        <footer className="cdr-modal-actions">
          <button type="button" className="cdr-secondary-button" onClick={onClose}>Cancelar</button>
          <button className="cdr-primary-button" disabled={saving}>{saving ? 'Salvando...' : 'Salvar referência'}</button>
        </footer>
      </form>
    </div>
  );
}

export default function CDRDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOperation, setShowOperation] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [opportunityData, setOpportunityData] = useState(null);
  const [opportunityTicker, setOpportunityTicker] = useState(null);
  const [opportunityLoading, setOpportunityLoading] = useState(true);
  const [opportunityRefreshing, setOpportunityRefreshing] = useState(false);
  const [opportunityError, setOpportunityError] = useState(null);
  const [opportunityFilters, setOpportunityFilters] = useState(EMPTY_OPPORTUNITY_FILTERS);
  const [appliedOpportunityFilters, setAppliedOpportunityFilters] = useState(EMPTY_OPPORTUNITY_FILTERS);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedExpiryEventId, setSelectedExpiryEventId] = useState(null);

  const load = useCallback(async () => {
    try {
      const payload = await getCDR();
      setData(payload);
      setError(null);
      setSelectedTicker(current => current || payload.tickerStates?.[0]?.ticker || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível carregar a CDR.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const loadOpportunities = useCallback(async ({ forceRefresh = false } = {}) => {
    setOpportunityLoading(true);
    setOpportunityRefreshing(true);
    try {
      const payload = await getCDROpportunities({
        ...(opportunityTicker ? { symbol: opportunityTicker } : {}),
        ...(forceRefresh ? { refresh: '1' } : {}),
        ...appliedOpportunityFilters,
      });
      setOpportunityData(payload);
      setOpportunityError(null);
      setSelectedContract(null);
    } catch (err) {
      setOpportunityError(err?.response?.data?.error || 'Não foi possível atualizar os contratos.');
    } finally {
      setOpportunityLoading(false);
      setOpportunityRefreshing(false);
    }
  }, [appliedOpportunityFilters, opportunityTicker]);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  const saveOperation = async payload => {
    if (editingOperation) await updateCDROperation(editingOperation.id, payload);
    else await createCDROperation(payload);
    setShowOperation(false);
    setEditingOperation(null);
    await load();
  };
  const saveSettings = async payload => {
    await updateCDRSettings(payload);
    setShowSettings(false);
    await load();
  };
  const removeOperation = async id => {
    if (!window.confirm('Excluir esta operação da CDR? Os ciclos e métricas serão recalculados.')) return;
    await deleteCDROperation(id);
    await load();
  };

  const metrics = data?.metrics || {};
  const settings = data?.settings || {};
  const availableCash = settings.initial_capital > 0
    ? settings.initial_capital + (metrics.netCashflow || 0)
    : null;
  const totalWealth = availableCash != null ? availableCash + (metrics.stockValue || 0) : null;
  const selected = useMemo(
    () => data?.tickerStates?.find(item => item.ticker === selectedTicker) || null,
    [data, selectedTicker],
  );
  const premiumProgress = settings.monthly_premium_goal > 0
    ? Math.min(100, (metrics.premiums || 0) / settings.monthly_premium_goal * 100)
    : null;
  const updateOpportunityFilter = (key, value) => {
    setOpportunityFilters(current => ({ ...current, [key]: value }));
  };
  const applyOpportunityFilters = () => {
    setAppliedOpportunityFilters({ ...opportunityFilters });
  };
  const clearOpportunityFilters = () => {
    setOpportunityFilters(EMPTY_OPPORTUNITY_FILTERS);
    setAppliedOpportunityFilters(EMPTY_OPPORTUNITY_FILTERS);
  };
  const selectOpportunityTicker = ticker => {
    setOpportunityTicker(ticker);
    setSelectedContract(null);
  };

  if (loading) return <div className="cdr-root"><div className="cdr-loading">Carregando CDR...</div></div>;
  if (error && !data) return <div className="cdr-root"><div className="cdr-error">{error}<button onClick={load}>Tentar novamente</button></div></div>;

  return (
    <div className="cdr-root">
      <section className="cdr-hero">
        <div>
          <span className="cdr-eyebrow">CDR / CICLO DINÂMICO DA RIQUEZA</span>
          <h1>Gestão da estratégia The Wheel</h1>
          <p>Registre cada evento do ciclo e acompanhe capital, prêmios, custo ajustado e o próximo passo possível. Nenhuma ordem é enviada pela plataforma.</p>
        </div>
        <div className="cdr-hero-actions">
          <button className="cdr-secondary-button" onClick={() => setShowSettings(true)}>Capital e meta</button>
          <button className="cdr-primary-button" onClick={() => { setEditingOperation(null); setShowOperation(true); }}>Registrar operação</button>
          <button className="cdr-refresh" onClick={load} aria-label="Atualizar CDR">↻</button>
        </div>
      </section>

      <section className="cdr-kpis" aria-label="Resumo da CDR">
        {[
          ['Patrimônio estimado', totalWealth, 'neutral'],
          ['Caixa disponível', availableCash, availableCash != null && availableCash < 0 ? 'negative' : 'positive'],
          ['Capital em PUTs', metrics.capitalCommitted, 'warning'],
          ['Ações em carteira', metrics.stockValue, 'neutral'],
          ['Prêmios recebidos', metrics.premiums, 'positive'],
          ['Resultado realizado', metrics.realizedResult, metrics.realizedResult >= 0 ? 'positive' : 'negative'],
          ['Resultado não realizado', metrics.unrealizedResult, metrics.unrealizedResult >= 0 ? 'positive' : 'negative'],
          ['Wheel Yield', metrics.wheelYield != null ? fmtPct(metrics.wheelYield) : '—', 'accent'],
        ].map(([label, value, tone]) => (
          <div className={`cdr-kpi ${tone}`} key={label}>
            <span>{label}</span>
            <strong>{typeof value === 'string' ? value : fmtBRL(value)}</strong>
          </div>
        ))}
      </section>

      <section className="cdr-submetrics">
        <span><strong>{metrics.openCycles || 0}</strong> ciclo(s) ativo(s)</span>
        <span><strong>{metrics.openPuts || 0}</strong> PUT(s) aberta(s)</span>
        <span><strong>{metrics.openCalls || 0}</strong> CALL(s) aberta(s)</span>
        <span>Sucesso dos ciclos: <strong>{metrics.cycleSuccessRate != null ? `${metrics.cycleSuccessRate.toFixed(0)}%` : '—'}</strong></span>
        <span>Duração média: <strong>{metrics.averageCycleDays != null ? `${metrics.averageCycleDays.toFixed(0)} dias` : '—'}</strong></span>
        {premiumProgress != null && <span>Meta de prêmios: <strong>{premiumProgress.toFixed(0)}%</strong></span>}
      </section>

      <section className="cdr-wheel-section">
        <div className="cdr-section-head">
          <div>
            <span className="cdr-eyebrow">WHEEL MAP</span>
            <h2>Onde cada ativo está na roda</h2>
          </div>
          <p>As fases são atualizadas pelo histórico de operações registrado.</p>
        </div>
        <div className="cdr-wheel-map">
          {(data?.phases || []).map(phase => (
            <div className="cdr-wheel-phase" key={phase.value} style={{ '--phase-color': PHASE_TONES[phase.value] }}>
              <div className="cdr-phase-head"><span>{phase.shortLabel}</span><strong>{phase.assets.length}</strong></div>
              <div className="cdr-phase-assets">
                {phase.assets.map(asset => (
                  <button className={`cdr-asset-card ${selectedTicker === asset.ticker ? 'selected' : ''}`} key={asset.ticker} onClick={() => setSelectedTicker(asset.ticker)}>
                    <strong>{asset.ticker}</strong>
                    <span>{asset.phaseLabel}</span>
                    {asset.openPutContracts > 0 && <small>PUT {asset.openPutContracts} contrato(s)</small>}
                    {asset.openCallContracts > 0 && <small>CALL {asset.openCallContracts} contrato(s)</small>}
                    {asset.shares > 0 && <small>{asset.shares} ação(ões)</small>}
                  </button>
                ))}
                {!phase.assets.length && <span className="cdr-phase-empty">Sem ativos</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ExpiryPlanningPanel
        planning={data?.planning}
        selectedEventId={selectedExpiryEventId}
        onSelectEvent={setSelectedExpiryEventId}
      />

      <OptionOpportunitiesPanel
        data={opportunityData}
        loading={opportunityLoading}
        refreshing={opportunityRefreshing}
        error={opportunityError}
        filters={opportunityFilters}
        onFilterChange={updateOpportunityFilter}
        onApply={applyOpportunityFilters}
        onClear={clearOpportunityFilters}
        onRefresh={() => loadOpportunities({ forceRefresh: true })}
        onSelectTicker={selectOpportunityTicker}
        selectedContract={selectedContract}
        onSelectContract={setSelectedContract}
      />

      <section className="cdr-grid">
        <div className="cdr-panel cdr-operation-panel">
          <div className="cdr-panel-head">
            <div><span className="cdr-eyebrow">OPERAÇÕES</span><h2>Diário da CDR</h2></div>
            <span>{data?.operations?.length || 0} registro(s)</span>
          </div>
          {!data?.operations?.length ? (
            <div className="cdr-empty">
              <strong>Comece o primeiro ciclo</strong>
              <p>Registre uma venda de PUT, uma compra de ações ou outro evento da estratégia The Wheel.</p>
              <button className="cdr-primary-button" onClick={() => { setEditingOperation(null); setShowOperation(true); }}>Registrar primeira operação</button>
            </div>
          ) : (
            <div className="cdr-operation-list">
              {[...data.operations].reverse().map(operation => (
                <article className="cdr-operation" key={operation.id}>
                  <div className="cdr-operation-date">{fmtDate(operation.trade_date)}</div>
                  <div className="cdr-operation-main">
                    <strong>{operation.operation_label}</strong>
                    <span>{operation.ticker}{operation.option_symbol ? ` · ${operation.option_symbol}` : ''}</span>
                    {operation.notes && <small>{operation.notes}</small>}
                  </div>
                  <div className={`cdr-operation-value ${operation.cashflow >= 0 ? 'positive' : 'negative'}`}>
                    {operation.cashflow >= 0 ? '+' : '−'}{fmtBRL(Math.abs(operation.cashflow))}
                  </div>
                  <div className="cdr-operation-actions">
                    <button onClick={() => { setEditingOperation(operation); setShowOperation(true); }}>Editar</button>
                    <button className="danger" onClick={() => removeOperation(operation.id)}>Excluir</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <aside className="cdr-panel cdr-detail-panel">
          <div className="cdr-panel-head">
            <div><span className="cdr-eyebrow">ATIVO</span><h2>{selected?.ticker || 'Selecione um ativo'}</h2></div>
            {selected && <span className="cdr-phase-badge" style={{ color: PHASE_TONES[selected.phase] }}>{selected.phaseLabel}</span>}
          </div>
          {selected ? (
            <>
              <div className="cdr-cost-stack">
                <div><span>Preço médio contábil</span><strong>{fmtBRL(selected.accountingAverage)}</strong></div>
                <div><span>Menos prêmios</span><strong>{fmtBRL(selected.adjustedPremiumAverage)}</strong></div>
                <div className="cdr-adjusted-cost"><span>Wheel Adjusted Cost</span><strong>{fmtBRL(selected.wheelAdjustedCost)}</strong><small>Prêmios + proventos</small></div>
              </div>
              <div className="cdr-detail-metrics">
                <span>Cotação <strong>{fmtBRL(selected.currentPrice)}</strong></span>
                <span>Posição <strong>{selected.shares} ação(ões)</strong></span>
                <span>Prêmios <strong>{fmtBRL(selected.premiums)}</strong></span>
                <span>Resultado <strong className={selected.realizedResult >= 0 ? 'positive' : 'negative'}>{fmtBRL(selected.realizedResult)}</strong></span>
              </div>
              <div className="cdr-cycle-mini">
                <span className="cdr-eyebrow">CICLOS DO ATIVO</span>
                {selected.cycles.map(cycle => (
                  <button key={cycle.id}>
                    <span>Ciclo #{cycle.cycleNumber} · {cycle.status === 'closed' ? 'encerrado' : 'ativo'}</span>
                    <strong className={cycle.result >= 0 ? 'positive' : 'negative'}>{fmtBRL(cycle.result)}</strong>
                    <small>{cycle.durationDays != null ? `${cycle.durationDays} dias` : '—'} · {fmtPct(cycle.returnPct)}</small>
                  </button>
                ))}
              </div>
            </>
          ) : <div className="cdr-empty"><p>Os detalhes do custo ajustado aparecem quando houver operações registradas.</p></div>}
        </aside>
      </section>

      <section className="cdr-panel cdr-cycles-panel">
        <div className="cdr-panel-head"><div><span className="cdr-eyebrow">CICLOS</span><h2>Histórico de ciclos The Wheel</h2></div><span>{data?.cycles?.length || 0} ciclo(s)</span></div>
        {!data?.cycles?.length ? <div className="cdr-empty"><p>Os ciclos surgem automaticamente a partir do histórico de operações por ativo.</p></div> : (
          <div className="cdr-cycle-table">
            <div className="cdr-cycle-row cdr-cycle-heading"><span>Ciclo</span><span>Fase</span><span>Prêmios</span><span>Resultado</span><span>Capital</span><span>Retorno</span><span>Duração</span></div>
            {data.cycles.map(cycle => (
              <div className="cdr-cycle-row" key={cycle.id}>
                <strong>{cycle.ticker} <small>#{cycle.cycleNumber}</small></strong>
                <span>{cycle.status === 'active' ? (cycle.phase === 'put_sold' ? 'PUT Vendida' : cycle.phase === 'call_sold' ? 'CALL Vendida' : 'Em andamento') : 'Concluído'}</span>
                <span>{fmtBRL(cycle.premiums)}</span>
                <span className={cycle.result >= 0 ? 'positive' : 'negative'}>{fmtBRL(cycle.result)}</span>
                <span>{fmtBRL(cycle.capital)}</span>
                <span>{fmtPct(cycle.returnPct)}</span>
                <span>{cycle.durationDays != null ? `${cycle.durationDays} dias` : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {showOperation && <OperationModal operation={editingOperation} onClose={() => { setShowOperation(false); setEditingOperation(null); }} onSave={saveOperation} />}
      {showSettings && <SettingsModal settings={settings} onClose={() => setShowSettings(false)} onSave={saveSettings} />}
    </div>
  );
}